import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    Signal,
    signal,
    WritableSignal,
} from '@angular/core';

import { ManagementMainPanelTitleComponent } from '@connect-services/client/components/management/management-main-panel-title/management-main-panel-title.component';
import { NotificationFieldInterventionSelectComponent } from '@connect-services/client/components/management/notification/notification-field-intervention-select/notification-field-intervention-select.component';
import { NotificationFormComponent } from '@connect-services/client/components/management/notification/notification-form/notification-form.component';
import { NotificationOperatorSelectComponent } from '@connect-services/client/components/management/notification/notification-operator-select/notification-operator-select.component';
import { ManagementNoPermissionComponent } from '@connect-services/client/pages/management/no-permission/management-no-permission.component';
import { FetchAllService } from '@connect-services/client/services/fetch-all.service';
import { GlobalSearchService } from '@connect-services/client/services/global-search.service';
import {
    FieldIntervention,
    FieldInterventionControllerGetRequestParams,
    FieldInterventionsService,
    PeopleService,
    Person,
} from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { OrderEnum, paginationMaxTake } from '@shared/client/models/pagination.model';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ManagementMainPanelTitleComponent,
        ManagementNoPermissionComponent,
        NotificationFormComponent,
        NotificationFieldInterventionSelectComponent,
        NotificationOperatorSelectComponent,
        RessourcesModule,
    ],
    selector: 'app-notification',
    templateUrl: './notification.component.html',
})
export class NotificationComponent {
    public readonly fieldInterventionSelectState: WritableSignal<Map<string, boolean>> = signal(
        new Map<string, boolean>(),
    );
    public readonly peopleSelectState: WritableSignal<Map<string, boolean>> = signal(new Map<string, boolean>());
    public readonly selectAllFieldIntervention = computed(
        () => !Array.from(this.fieldInterventionSelectState().values()).some((state) => !state),
    );
    public readonly selectAllPeople = computed(
        () => !Array.from(this.peopleSelectState().values()).some((state) => !state),
    );

    protected readonly selectedFieldInterventionIds: Signal<string[]> = computed((): string[] =>
        Array.from(this.fieldInterventionSelectState().entries())
            .filter(([, isSelected]) => isSelected)
            .map(([id]) => id),
    );
    protected readonly selectedOperatorIds: Signal<string[]> = computed((): string[] =>
        Array.from(this.peopleSelectState().entries())
            .filter(([, isSelected]) => isSelected)
            .map(([id]) => id),
    );

    private readonly fetchAllService = inject(FetchAllService);
    private readonly fieldInterventionsService = inject(FieldInterventionsService);
    private readonly globalSearchService = inject(GlobalSearchService);
    private readonly peopleService = inject(PeopleService);

    public constructor() {
        effect(async () => {
            const fieldInterventions: FieldIntervention[] = await this.fetchAllFieldIntervention();

            this.fieldInterventionSelectState.set(
                fieldInterventions.reduce<Map<string, boolean>>(
                    (acc: Map<string, boolean>, fieldIntervention: FieldIntervention): Map<string, boolean> => {
                        acc.set(fieldIntervention.id, false);

                        return acc;
                    },
                    new Map<string, boolean>(),
                ),
            );
        });

        effect(async () => {
            const people: Person[] = await this.fetchAllPeople();

            this.peopleSelectState.set(
                people.reduce<Map<string, boolean>>(
                    (acc: Map<string, boolean>, person: Person): Map<string, boolean> => {
                        acc.set(person.id, false);

                        return acc;
                    },
                    new Map<string, boolean>(),
                ),
            );
        });

        this.globalSearchService.setSearchProperties();
    }

    public async toggleSelection(all: boolean, dataType: 'FieldIntervention' | 'People', id?: string): Promise<void> {
        if (all) {
            this.toggleAll(dataType);
        }

        if (id) {
            this.toggleById(id, dataType);
        }

        if (dataType !== 'FieldIntervention') {
            return;
        }

        await this.togglePeopleByFieldIntervention();
    }

    private fetchAllFieldIntervention(): Promise<FieldIntervention[]> {
        return this.fetchAllService.getAll<FieldInterventionControllerGetRequestParams, FieldIntervention>(
            (params) =>
                this.fieldInterventionsService.fieldInterventionControllerGet({
                    ...params,
                }),
            {
                isCaseSensitive: true,
                order: OrderEnum.ASC,
                orderBy: 'name',
                take: paginationMaxTake,
            },
        );
    }

    private fetchAllPeople(fieldInterventionIds?: string[]): Promise<Person[]> {
        if (!fieldInterventionIds) {
            return this.fetchAllService.getAll(this.peopleService.personControllerGet.bind(this.peopleService), {
                order: 'ASC',
                orderBy: 'givenName',
                take: paginationMaxTake,
            });
        }

        return this.fetchAllService.getAll(
            this.peopleService.personControllerGetFieldInterventionOperators.bind(this.peopleService),
            {
                fieldInterventionIds: fieldInterventionIds,
                order: 'ASC',
                orderBy: 'givenName',
                take: paginationMaxTake,
            },
        );
    }

    private toggleAll(dataType: 'FieldIntervention' | 'People'): void {
        switch (dataType) {
            case 'FieldIntervention':
                this.fieldInterventionSelectState.set(
                    this.turnAllStateTo(this.fieldInterventionSelectState(), !this.selectAllFieldIntervention()),
                );

                break;
            case 'People':
                this.peopleSelectState.set(this.turnAllStateTo(this.peopleSelectState(), !this.selectAllPeople()));
                this.fieldInterventionSelectState.set(this.turnAllStateTo(this.fieldInterventionSelectState(), false));
                break;
        }
    }

    private toggleById(id: string, dataType: 'FieldIntervention' | 'People'): void {
        let stateMap: Map<string, boolean>;

        switch (dataType) {
            case 'FieldIntervention':
                stateMap = new Map(this.fieldInterventionSelectState());
                stateMap.set(id, !this.fieldInterventionSelectState().get(id));

                this.fieldInterventionSelectState.set(stateMap);
                break;
            case 'People':
                stateMap = new Map(this.peopleSelectState());
                stateMap.set(id, !this.peopleSelectState().get(id));

                this.peopleSelectState.set(stateMap);
                this.fieldInterventionSelectState.set(this.turnAllStateTo(this.fieldInterventionSelectState(), false));
                break;
        }
    }

    private async togglePeopleByFieldIntervention(): Promise<void> {
        const stateMap = this.turnAllStateTo(this.peopleSelectState(), false);

        const people: Person[] = await this.fetchAllPeople(this.selectedFieldInterventionIds());

        for (const person of people) {
            if (stateMap.has(person.id)) {
                stateMap.set(person.id, true);
            }
        }

        this.peopleSelectState.set(stateMap);
    }

    private turnAllStateTo(map: Map<string, boolean>, value: boolean): Map<string, boolean> {
        return new Map(Array.from(map.keys()).map((key) => [key, value]));
    }
}
